import React from 'react';
import { Button, Input, Modal, Select } from '@avila-tek/ui/src';
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation,
} from '@apollo/client';
import { useRouter } from 'next/router';
import { ACTIONS } from './reducer';
import { banksInfo, paymentMethods } from '../../../static';
import {
  DocumentModel,
  PayableRequestInfo,
  TPayablePaymentForm,
} from '../../../models';
import { PAY_PAYABLE_ACCOUNT } from '../../../graphql/mutation';
import { validatePayablePaymentForm } from '../../../lib/formValidations';
import { useNotify } from '../../../hooks';
import DocumentFormWithoutCard from '../document/DocumentFormWithoutCard';

interface ResetPasswordModalProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRestarValues?: React.Dispatch<React.SetStateAction<boolean>>;
  amount: number;
  orderProductsIds: Array<string>;
  refreshData: (variables?: Partial<OperationVariables>) => Promise<
    ApolloQueryResult<{
      account: PayableRequestInfo;
    }>
  >;
}

function PayPayableModal({
  isOpen,
  setOpen,
  setRestarValues,
  amount,
  orderProductsIds,
  refreshData,
}: ResetPasswordModalProps) {
  const [documents, setDocuments] = React.useState<
    Array<Partial<DocumentModel>>
  >([]);
  const [state, setState] = React.useState<TPayablePaymentForm>({
    bank: '',
    paymentmethod: '',
    amount,
    reference: 0,
    image: '',
    orderProductsIds,
    changetype: 0,
    paymentdate: '',
  });

  const router = useRouter();
  const notify = useNotify();

  const [payAccountByAlly] = useMutation<{
    payAccountByAlly: {
      total: number;
      orderPayments: { paymentStatus: string }[];
    };
  }>(PAY_PAYABLE_ACCOUNT);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      // validate form
      const payablePaymentError = validatePayablePaymentForm({
        ...state,
      });
      if (payablePaymentError) {
        notify(payablePaymentError, 'error');
        return;
      }

      const { data } = await payAccountByAlly({
        variables: {
          data: {
            accountId: router.query.payableId as string,
            orderProducts: state.orderProductsIds,
            paymentRef: {
              amount: state.amount,
              reference: state.reference.toString(),
              image: state.image,
              metadata: {
                bank: state.bank,
                paymentMethod: state.paymentmethod,
                changetype:
                  state.paymentmethod === 'Zelle' ? 'N/A' : state.changetype,
              },
            },
            paymentDate: state.paymentdate,
          },
        },
      });
      // Early return if there is no data
      if (!data?.payAccountByAlly) {
        notify('Ha ocurrido un error al pagar la cuenta', 'error');
        return;
      }

      // Si la mutación fue exitosa
      notify('Ha sido pagada la cuenta con éxito', 'success');

      // Close modal
      setOpen(false);

      // Reset state
      setState({
        bank: '',
        paymentmethod: '',
        amount,
        reference: 0,
        image: '',
        orderProductsIds,
        changetype: 0,
        paymentdate: '',
      });

      // Document reset
      setDocuments([]);

      // Refresh data
      refreshData();

      // Reset values
      setRestarValues((prev) => !prev);
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'PAYMENTMETHOD') {
      setState((prevState) => ({
        ...prevState,
        bank: '',
      }));
      if (value === 'Zelle') {
        setState((prevState) => ({
          ...prevState,
          changetype: null,
        }));
      }
    }
    setState((prevState) => ({
      ...prevState,
      [name.toLowerCase()]: value,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name.toLowerCase()]: value,
    }));
  };

  const handleChangeFloat = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name.toLowerCase()]: parseFloat(value),
    }));
  };

  const handleChangeNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name.toLowerCase()]: parseInt(value),
    }));
  };

  React.useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      image: documents[0]?.src as string,
    }));
  }, [documents]);

  React.useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      amount,
      orderProductsIds,
    }));
  }, [orderProductsIds, amount]);

  const paymentMethodsToShowBanksSelector = ['Transferencia', 'Pago móvil'];
  const showBankSelector = paymentMethodsToShowBanksSelector.includes(
    state.paymentmethod
  );

  return (
    <Modal isOpen={isOpen} setOpen={setOpen} className="md:max-w-[60%]">
      <form method="POST" onSubmit={onSubmit}>
        <div className="bg-white rounded-lg px-6 py-3 border space-y-5">
          <Modal.Title
            title={`Reporte de pago (Monto: ${amount}$)`}
            setIsOpen={setOpen}
          />
          <div className="w-full flex md:flex-row flex-col gap-5">
            <div className="w-full">
              <Select
                value={state.paymentmethod}
                label="Método de pago"
                onChange={handleSelectChange}
                name={ACTIONS.PAYMENT_METHOD}
                required
              >
                <option value="" disabled>
                  Seleccione un Método de Pago
                </option>
                {paymentMethods.map((option) => (
                  <option value={option.type} key={option.type}>
                    {option.type}
                  </option>
                ))}
              </Select>
            </div>
            {showBankSelector ? (
              <div className="w-full">
                <Select
                  value={state.bank}
                  label="Banco"
                  onChange={handleSelectChange}
                  name={ACTIONS.BANK}
                  required
                >
                  <option value="" disabled>
                    Seleccione un banco
                  </option>
                  {banksInfo.map((option) => (
                    <option value={option.code} key={option.code}>
                      {option.code} - {option.name}
                    </option>
                  ))}
                </Select>
              </div>
            ) : (
              <div className="w-full">
                <Input
                  name={ACTIONS.BANK}
                  value={state.bank}
                  label="Banco"
                  type="text"
                  placeholder="Ingrese el banco de la transacción"
                  onChange={handleChange}
                  required
                />
              </div>
            )}
          </div>
          <div className="w-full flex md:flex-row flex-col gap-5">
            {state.paymentmethod !== 'Zelle' ? (
              <div className="w-full">
                <Input
                  name={ACTIONS.CHANGETYPE}
                  value={state.changetype}
                  label="Tipo de cambio"
                  type="number"
                  placeholder="Ingrese Tipo de cambio"
                  onChange={handleChangeFloat}
                  required
                />
              </div>
            ) : null}
            <div className="w-full">
              <Input
                name={ACTIONS.REFERENCE}
                value={state.reference}
                label="Referencia"
                type="number"
                placeholder="Ingrese Referencia de trasacción"
                onChange={handleChangeNumber}
                required
              />
            </div>
          </div>
          <div className="w-full ">
            <Input
              name={ACTIONS.PAYMENTDATE}
              value={state.paymentdate as string}
              onChange={handleChange}
              label="Fecha de pago"
              type="date"
              className="bg-white"
            />
          </div>
          <div className="w-full">
            <span className="text-gray-300 text-sm font-arial font-extrabold mb-2 tracking-wider ">
              Comprobante de pago
            </span>
            <DocumentFormWithoutCard
              documents={documents}
              updateURLs={setDocuments}
              maximumAmount={1}
            />
          </div>
          <div className="flex flex-col px-6 pb-6">
            <Button className="">Realizar pago</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default PayPayableModal;
